import React, {
  createContext, useCallback, useContext, useEffect, useState,
} from 'react';
import s from './Toast.module.css';

// A toast: one short sentence at the bottom of the screen that goes away on
// its own.
//
// For the answer to a click that has nowhere better to go (#568) — the
// check-your-email screen answers its two buttons here. Not for anything a
// teacher has to act on later: a refused write stays on screen until it is
// dealt with (SyncNotices), and a form's refusal stays beside the form. A
// toast is for "that worked" and "that did not, try again".
//
// ── Shape ─────────────────────────────────────────────────────────────────
//
// One at a time; a second replaces the first rather than stacking, because two
// answers to the same button pressed twice are one answer. The live region is
// mounted whether or not there is a message, because a region that appears
// with its text already in it is not announced by most screen readers.
//
// The provider sits in App.jsx above every route, so the hook can be called by
// a page as well as by the shell, and a toast survives the navigation it may
// have caused.

/** How long a toast stays, in milliseconds. Long enough to read two short
 *  sentences, short enough not to be furniture. */
export const TOAST_MS = 5000;

// No provider is a wiring mistake, and it is refused loudly at the call rather
// than swallowed: a toast that silently goes nowhere is the failure this
// component exists to prevent, moved somewhere nobody would look.
const ToastContext = createContext(() => {
  throw new Error('useToast() was called with no <ToastProvider> above it');
});

export function ToastProvider({ children }) {
  // `seq` makes a repeat of the same sentence a new toast — the timer restarts
  // and a screen reader hears it again — rather than a no-op state update.
  const [current, setCurrent] = useState(null);

  const show = useCallback((text) => {
    setCurrent((prev) => ({ text: String(text), seq: (prev?.seq ?? 0) + 1 }));
  }, []);

  useEffect(() => {
    if (!current) return undefined;
    const timer = setTimeout(() => setCurrent(null), TOAST_MS);
    return () => clearTimeout(timer);
  }, [current]);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className={s.region} role="status" aria-live="polite">
        {current ? (
          <div key={current.seq} className={s.toast}>
            <span className={s.text}>{current.text}</span>
            <button
              type="button"
              className={s.close}
              aria-label="Dismiss"
              onClick={() => setCurrent(null)}
            >
              ×
            </button>
          </div>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}

/** `const toast = useToast(); toast('Sent.')` */
export function useToast() {
  return useContext(ToastContext);
}
